import { Icon } from '@components/style';
import { theme } from '@theme';
import debounce from 'lodash/debounce';
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useCheckDevice } from '../../../hooks/UseCheckDevice';
import { useOutsideClickDetection } from '../../../hooks/UseOutsideClickDetection';

type SearchBoxProps = {
  inputText: string;
  isOpen: boolean;
  placeholder: string;
  searchCallback: (searchText: string) => void;
  showCloseIcon?: boolean;
  searchIconCallback?: (isOpen) => void;
};

const SearchBox: FC<SearchBoxProps> = ({
  inputText,
  isOpen,
  placeholder,
  searchCallback,
  showCloseIcon,
  searchIconCallback,
}) => {
  const [isShow, setIsShow] = useState<boolean>(isOpen);
  const inputRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string>(inputText);
  const wrapperRef = useRef<HTMLElement>(null);
  useOutsideClickDetection(wrapperRef, setIsShow);
  useOutsideClickDetection(wrapperRef, searchIconCallback);
  const [isSearchClear, setIsSearchClear] = useState<boolean>(true);
  const { isMobileDevice } = useCheckDevice();

  useEffect(() => {
    if (isShow) {
      inputRef.current.focus();
    }
  }, [isShow]);

  useEffect(() => {
    inputRef.current.value = inputText;
    if (inputText) {
      setMessage(inputText);
      setIsSearchClear(false);
    } else {
      setMessage('');
      setIsSearchClear(true);
    }
  }, [inputText]);

  useEffect(() => {
    if (isOpen) {
      onIconClick();
    }
  }, [isOpen]);

  const onIconClick = (): void => {
    setIsShow(true);
    if (searchIconCallback) {
      searchIconCallback(true);
    }
  };

  const onFilterBoxChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setMessage(value);
    if (value.length === 0 && !isSearchClear) {
      setIsSearchClear(true);
      searchCallback('');
    } else if (value.length > 2) {
      setIsSearchClear(false);
      searchCallback(e.target.value);
    }
  };

  const debounceOnFilterChange = useCallback(debounce(onFilterBoxChange, 500), [isSearchClear]);

  const onCloseIconClick = () => {
    setIsShow(false);
    searchCallback('');
    if (searchIconCallback) {
      searchIconCallback(false);
    }
  };

  return (
    <Wrapper
      data-testid='portfolio-finder'
      ref={message === '' ? wrapperRef : null}
      className={isMobileDevice && isShow ? 'width-hundred' : ''}
    >
      <InputBackground
        isMobileDevice={isMobileDevice && !isShow}
        className={`portfolio-finder-input-wrapper ${isShow ? 'searchBox-input-border-display' : 'searchBox-border-none'} ${isMobileDevice && isShow ? 'width-hundred' : ''} ${
          !isMobileDevice ? 'desktop' : ''
        }`}
      >
        <Icon
          type='loupe'
          size={17}
          data-testid='loupe'
          color={theme.palette.grayTwo}
          onClick={onIconClick}
          additionalClassName={`${isShow ? 'searchBox-border-none' : 'searchBox-icon-border-display'}`}
        />
        {
          <input
            type='text'
            className={isShow ? 'input-open' : 'input-close'}
            placeholder={placeholder}
            data-testid='portfolio-finder-input'
            ref={inputRef}
            onChange={debounceOnFilterChange}
          />
        }
        {isShow && showCloseIcon && (
          <Icon
            data-testid='close-icon'
            type='x'
            size={theme.typography.h2}
            color={theme.palette.basicGray}
            additionalClassName={`${isShow ? 'searchBox-border-none' : 'searchBox-icon-border-display'}`}
            onClick={onCloseIconClick}
          />
        )}
      </InputBackground>
    </Wrapper>
  );
};

export default SearchBox;

type InputBackgroundProps = {
  isMobileDevice: boolean;
};
const Wrapper = styled.section`
  display: inline-flex;
`;

const InputBackground = styled.div<InputBackgroundProps>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding: 0;
  margin: 0;
  background: #ffffff;
  border-radius: 4px;
  position: relative;
  top: 4px;

  &.desktop {
    width: 187px;
  }

  & > span {
    align-self: center;
    background: white;
    border-radius: 4px;
    display: flex;
    width: 25px;
    height: 25px;
    padding: 3px;
    cursor: pointer;
    align-items: center;
    line-height: 0px;
  }
  & > input {
    border: none;
    height: 25px;
    background-color: transparent;
    outline: none;
    font-weight: 400;
    font-size: 14px;
    caret-color: #0a72cb;
    line-height: 15px;
    display: ${(props) => (props.isMobileDevice ? 'contents' : '')};
  }
`;
